import { Asset } from 'base/asset';
import { sortComparatorAsc } from 'base/helpers';
import { TextSmall, TextTiny, Title1, Title5 } from 'base/ui/typography';
import classNames from 'classnames';
import React from 'react';
import styles from './avatar.css';

type AvatarSize = 'tiny' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge';

type Props = {
  /**
   * tiny - 16 x 16
   * small - 24 x 24
   * medium - 28 x 28
   * large - 32 x 32
   * xlarge - 48 x 48
   * xxlarge - 80 x 80
   */
  size: AvatarSize;
  shape?: 'circle' | 'square';
  isAnonymous?: boolean;
  color?: string;
  firstName?: string;
  lastName?: string;
  avatar?: Asset;
  bordered?: boolean;
  onClick?: () => void;
};

function getSizeConfig(size: AvatarSize) {
  switch (size) {
    case 'tiny':
      return {
        style: styles.tiny,
        Text: TextTiny,
      };
    case 'small':
      return {
        style: styles.small,
        Text: TextTiny,
      };
    case 'medium':
      return {
        style: styles.medium,
        Text: TextSmall,
      };
    case 'large':
      return {
        style: styles.large,
        Text: TextSmall,
      };
    case 'xlarge':
      return {
        style: styles.xlarge,
        Text: Title5,
      };
    case 'xxlarge':
      return {
        style: styles.xxlarge,
        Text: Title1,
      };
    default:
      throw new Error(`Unknown size: ${size}`);
  }
}

export const Avatar = React.forwardRef<HTMLElement, Props>(
  (
    {
      size,
      shape = 'circle',
      firstName,
      lastName,
      color,
      avatar,
      onClick,
      bordered,
      isAnonymous,
    },
    ref,
  ) => {
    const [errorLoadingImage, setError] = React.useReducer(() => false, true);
    const { style, Text } = getSizeConfig(size);
    if (avatar) {
      const images = avatar.files.sort(sortComparatorAsc(({ size }) => size));
      return (
        <img
          ref={ref as React.Ref<HTMLImageElement>}
          key={images[0].url}
          src={images[0].url}
          alt="avatar"
          className={classNames(
            styles.imageAvatar,
            style,
            shape === 'square' && styles.square,
            errorLoadingImage && styles.hideAltText,
          )}
          style={bordered ? { border: `2px solid ${color}` } : undefined}
          onClick={onClick}
          onError={setError}
        />
      );
    }
    const initials = isAnonymous
      ? 'A'
      : `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}` || ' ';
    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        onClick={onClick}
        className={classNames(
          styles.defaultAvatar,
          style,
          shape === 'square' && styles.square,
        )}
        style={{ backgroundColor: color }}
      >
        <Text tag="p" color="white">
          {initials.toUpperCase()}
        </Text>
      </div>
    );
  },
);
